'use client';

import { Modal } from '@wishket/design-system';
import { useRef, FormEvent, ChangeEvent } from 'react';

import useAiPrompt from './useAiPrompt';
import useThumbnail from './useThumbnail';

import { usePatchCollectionThumbnailMutation } from '@/entities/collection/models/collectionDetail.model';

import { useDeviceCheck } from '@/shared/hooks';
import { PATH } from '@/shared/constants';
import { CollectionThumbnailForm } from '@/features/Collection';

const MAX_FILE_SIZE = 1 * 1024 * 1024;
const DEFAULT_THUMBNAIL_IMAGE_URL = `${PATH.MEDIA_YOZM_IMAGE}/default_collection_thumbnail.svg`;

interface CollectionThumbnailModalProps {
  id: number;
  isOpen: boolean;
  image: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CollectionThumbnailEdit = ({
  id,
  image,
  isOpen,
  onClose,
  onSuccess: onSubmitSuccess,
}: CollectionThumbnailModalProps) => {
  const fileUploadInputRef = useRef<HTMLInputElement>(null);

  const {
    prompt,
    aiSelected,
    aiUnselected,
    isPromptVisible,
    init: promptInit,
    handlePromptChange,
    mutate: generateAiThumbnail,
    isPending: isPromptLoading,
    setPrompt,
  } = useAiPrompt();
  const {
    value,
    onChange,
    getCroppedFile,
    init: thumbnailInit,
  } = useThumbnail({
    image,
  });

  const { mutate, isPending } = usePatchCollectionThumbnailMutation();

  const openImageSelector = () => {
    aiUnselected();
    fileUploadInputRef.current?.click();
  };

  const uploadImage = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    const isFileSelected = files && files.length > 0;

    if (!isFileSelected) return;

    const uploadedImageFile = files[0];

    if (uploadedImageFile.size > MAX_FILE_SIZE) {
      alert('파일은 1MB 이하만 가능합니다.');
      event.target.value = '';
      return;
    }
    if (value.thumbnail.imageSrc !== DEFAULT_THUMBNAIL_IMAGE_URL) {
      URL.revokeObjectURL(value.thumbnail.imageSrc);
    }

    const uploadedImageSrc = URL.createObjectURL(uploadedImageFile);

    onChange.thumbnail.setImage({
      imageUrl: null,
      imageType: 'file',
      imageSrc: uploadedImageSrc,
      imageFile: uploadedImageFile,
    });
  };

  const submitImage = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isZoomChanged = value.thumbnail.zoom > 1;
    const isDefaultImage =
      !image && value.thumbnail.imageSrc === DEFAULT_THUMBNAIL_IMAGE_URL;
    const isCropChanged =
      value.thumbnail.crop.x > 0 || value.thumbnail.crop.y > 0;
    const isNotChanged =
      image === value.thumbnail.imageSrc && !isZoomChanged && !isCropChanged;

    if (isPending || isDefaultImage || isNotChanged) return;

    const isReset =
      image && value.thumbnail.imageSrc === DEFAULT_THUMBNAIL_IMAGE_URL;

    let croppedFile: File | null | undefined = null;
    if (!isReset) {
      croppedFile = await getCroppedFile();
    }

    const imageFile = croppedFile ?? value.thumbnail.imageFile;

    mutate(
      {
        id,
        imageFile,
      },
      {
        onSuccess: () => {
          promptInit();
          thumbnailInit();
          onSubmitSuccess();
        },

        onError: () => {
          alert('잠시 후 다시 시도해주세요.');
        },
      }
    );
    URL.revokeObjectURL(value.thumbnail.imageSrc);
  };

  const handlePromptSubmit = async (prompt: string) => {
    if (!prompt || isPromptLoading) return;

    generateAiThumbnail(
      { prompt },
      {
        onSuccess: ({ data }) => {
          onChange.thumbnail.setImage({
            imageFile: null,
            imageType: 'url',
            imageSrc: data.data.imageUrl,
            imageUrl: data.data.imageUrl,
          });
        },

        onError: () => {
          alert('잠시 후 다시 시도해주세요.');
        },
      }
    );
  };

  const handleDelete = () => {
    if (!isPromptLoading) {
      promptInit();
      thumbnailInit();
      if (fileUploadInputRef.current) {
        fileUploadInputRef.current.value = '';
      }
    }
  };

  const handleClose = () => {
    if (isPending) return;

    if (value.thumbnail.imageSrc) {
      URL.revokeObjectURL(value.thumbnail.imageSrc);
    }

    onClose();
    promptInit();
    thumbnailInit();
  };

  const handlePromptClick = (prompt: string) => {
    setPrompt(prompt);
    handlePromptSubmit(prompt);
  };

  if (!isOpen) return null;

  return (
    <>
      <input
        type="file"
        className="hidden"
        accept=".jpg,.jpeg,.png,"
        ref={fileUploadInputRef}
        onChange={uploadImage}
      />
      <CollectionThumbnailForm
        prompt={prompt}
        slider={value.slider}
        onClose={handleClose}
        onSubmit={submitImage}
        thumbnail={value.thumbnail}
        aiSelected={aiSelected}
        resetPrompt={promptInit}
        promptSubmit={handlePromptSubmit}
        onPromptChange={handlePromptChange}
        onSliderChange={onChange.slider}
        deleteThumbnail={handleDelete}
        isPromptVisible={isPromptVisible}
        isPromptLoading={isPromptLoading}
        onThumbnailChange={onChange.thumbnail}
        openImageSelector={openImageSelector}
        handlePromptClick={handlePromptClick}
      />
    </>
  );
};

const CollectionThumbnailModal = ({
  id,
  image,
  isOpen,
  onClose,
  onSuccess: onSubmitSuccess,
}: CollectionThumbnailModalProps) => {
  const { isDesktop } = useDeviceCheck();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      variant={isDesktop ? 'default' : 'full'}
    >
      <CollectionThumbnailEdit
        isOpen={isOpen}
        id={id}
        image={image}
        onClose={onClose}
        onSuccess={onSubmitSuccess}
      />
    </Modal>
  );
};

export default CollectionThumbnailModal;
